import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  useCapitalInvestments,
  useCreateCapitalInvestment,
  useUpdateCapitalInvestment,
  useDeleteCapitalInvestment,
  useCompleteInvestment,
} from '../hooks/useCapitalInvestments';
import { formatCurrency } from '../utils/format';
import {
  Avatar,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  InputAdornment,
  LinearProgress,
  MenuItem,
  Paper,
  Select,
  Snackbar,
  Alert,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import { DataGrid, GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  AccountBalance as InvestmentIcon,
  TrendingDown as DepreciationIcon,
  Savings as BookValueIcon,
  LocalShipping as ShippingIcon,
  PlaylistAdd as AddCostIcon,
  Construction as ConstructionIcon,
  CheckCircle as CheckCircleIcon,
  OpenInNew as OpenDetailIcon,
} from '@mui/icons-material';

// ── Constants ──────────────────────────────────────────────────────────────

const CATEGORIES = [
  'Equipment',
  'Infrastructure',
  'Vehicle',
  'Land',
  'Livestock',
  'Other',
] as const;

const DEPRECIATION_METHODS = [
  { value: 'straight_line', label: 'Straight Line' },
  { value: 'declining_balance', label: 'Declining Balance' },
] as const;

const CATEGORY_CONFIG: Record<string, { color: string; bg: string }> = {
  Equipment: { color: '#ff9800', bg: 'rgba(255,152,0,0.12)' },
  Infrastructure: { color: '#42a5f5', bg: 'rgba(66,165,245,0.12)' },
  Vehicle: { color: '#66bb6a', bg: 'rgba(102,187,106,0.12)' },
  Land: { color: '#26a69a', bg: 'rgba(38,166,154,0.12)' },
  Livestock: { color: '#ab47bc', bg: 'rgba(171,71,188,0.12)' },
  Other: { color: '#78909c', bg: 'rgba(120,144,156,0.12)' },
};

const EMPTY_FORM = {
  name: '',
  category: 'Equipment',
  purchase_date: '',
  currency: 'GHS',
  cost: '',
  exchange_rate: '',
  cost_ghs: '',
  useful_life_years: '5',
  salvage_value_ghs: '0',
  depreciation_method: 'straight_line',
  notes: '',
  status: 'active',
};

const EMPTY_BREAKDOWN = {
  shipping: '', // USD always
  shipping_rate: '', // GHS/USD rate — only used when base currency is GHS
  import_duty: '', // GHS
  clearing_fees: '', // GHS
  local_transport: '', // GHS
  installation: '', // GHS
  other: '', // GHS
};

const BREAKDOWN_LABELS: Record<string, string> = {
  shipping: 'Shipping / Freight (USD)',
  import_duty: 'Import Duty',
  clearing_fees: 'Clearing Fees',
  local_transport: 'Local Transport',
  installation: 'Installation',
  other: 'Other',
};

// ── Helpers ────────────────────────────────────────────────────────────────

const formatUSD = (amount: number | string) => {
  const n = typeof amount === 'string' ? parseFloat(amount) : amount;
  if (isNaN(n)) return '$ 0.00';
  return `$ ${n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

const fmtDate = (d: string) =>
  d
    ? new Date(d).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
    : '—';

// shipping is USD — converted separately using effectiveRate; all other fields are GHS
const sumBreakdownGhs = (bd: typeof EMPTY_BREAKDOWN, shippingRate: number): number => {
  const shippingGhs = (parseFloat(bd.shipping) || 0) * shippingRate;
  const ghsFields = (
    ['import_duty', 'clearing_fees', 'local_transport', 'installation', 'other'] as const
  ).reduce((s, k) => s + (parseFloat(bd[k]) || 0), 0);
  return shippingGhs + ghsFields;
};

// ── Sub-components ─────────────────────────────────────────────────────────

const StatCard: React.FC<{
  label: string;
  value: React.ReactNode;
  sub?: string;
  accent: string;
  Icon: React.ElementType;
}> = ({ label, value, sub, accent, Icon }) => (
  <Paper
    sx={{
      p: 2.5,
      height: '100%',
      background: 'linear-gradient(145deg, #181818 0%, #1d1d1d 100%)',
      border: `1px solid ${accent}28`,
      borderRadius: 2.5,
      position: 'relative',
      overflow: 'hidden',
      transition: 'all 0.2s',
      '&:hover': {
        transform: 'translateY(-2px)',
        boxShadow: `0 8px 24px rgba(0,0,0,0.5), 0 0 0 1px ${accent}22`,
      },
      '&::before': {
        content: '""',
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        height: 3,
        background: `linear-gradient(90deg, ${accent}, ${accent}44)`,
      },
    }}
  >
    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
      <Box>
        <Typography
          variant="caption"
          sx={{
            color: '#666',
            fontWeight: 700,
            letterSpacing: '1px',
            fontSize: '0.62rem',
            display: 'block',
            mb: 0.75,
          }}
        >
          {label.toUpperCase()}
        </Typography>
        <Typography variant="h5" sx={{ color: accent, fontWeight: 800, lineHeight: 1.1 }}>
          {value}
        </Typography>
        {sub && (
          <Typography
            variant="caption"
            sx={{ color: '#555', fontSize: '0.68rem', mt: 0.5, display: 'block' }}
          >
            {sub}
          </Typography>
        )}
      </Box>
      <Avatar
        sx={{
          bgcolor: `${accent}14`,
          color: accent,
          width: 40,
          height: 40,
          border: `1px solid ${accent}22`,
        }}
      >
        <Icon fontSize="small" />
      </Avatar>
    </Box>
  </Paper>
);

// ── Form dialog ────────────────────────────────────────────────────────────

const InvestmentForm: React.FC<{
  open: boolean;
  initial: any;
  onClose: () => void;
  onSave: (data: any) => void;
  saving: boolean;
}> = ({ open, initial, onClose, onSave, saving }) => {
  const [form, setForm] = useState<any>(initial);
  const [breakdown, setBreakdown] = useState<typeof EMPTY_BREAKDOWN>(EMPTY_BREAKDOWN);

  React.useEffect(() => {
    setForm(initial);
    if (
      initial?.cost_breakdown &&
      Object.values(initial.cost_breakdown).some((v) => Number(v) > 0)
    ) {
      setBreakdown({
        shipping: String(initial.cost_breakdown.shipping || ''),
        shipping_rate: String(initial.cost_breakdown.shipping_rate || ''),
        import_duty: String(initial.cost_breakdown.import_duty || ''),
        clearing_fees: String(initial.cost_breakdown.clearing_fees || ''),
        local_transport: String(initial.cost_breakdown.local_transport || ''),
        installation: String(initial.cost_breakdown.installation || ''),
        other: String(initial.cost_breakdown.other || ''),
      });
    } else {
      setBreakdown(EMPTY_BREAKDOWN);
    }
  }, [initial]);

  const set = (field: string, val: any) => setForm((f: any) => ({ ...f, [field]: val }));
  const setBd = (field: string, val: string) => setBreakdown((b) => ({ ...b, [field]: val }));

  const costNum = parseFloat(form.cost) || 0;
  const rateNum = parseFloat(form.exchange_rate) || 0;
  const baseGhs = form.currency === 'USD' ? costNum * rateNum : costNum;
  // shipping rate: use form's exchange rate (USD purchase) or the inline breakdown rate (GHS purchase)
  const effectiveShippingRate =
    form.currency === 'USD' ? rateNum : parseFloat(breakdown.shipping_rate) || 0;
  const breakdownSum = sumBreakdownGhs(breakdown, effectiveShippingRate);
  const totalGhs = baseGhs + breakdownSum;

  const handleCurrencyChange = (_: any, val: string | null) => {
    if (!val) return;
    set('currency', val);
    if (val === 'GHS') set('exchange_rate', '');
  };

  const handleSubmit = () => {
    const hasBreakdown = !isConstruction && breakdownSum > 0;
    const payload = {
      ...form,
      cost: isConstruction ? 0 : parseFloat(form.cost) || 0,
      exchange_rate:
        !isConstruction && form.currency === 'USD' ? parseFloat(form.exchange_rate) || null : null,
      cost_ghs: isConstruction ? 0 : totalGhs,
      cost_breakdown: hasBreakdown
        ? {
            shipping: parseFloat(breakdown.shipping) || 0,
            shipping_rate: effectiveShippingRate,
            import_duty: parseFloat(breakdown.import_duty) || 0,
            clearing_fees: parseFloat(breakdown.clearing_fees) || 0,
            local_transport: parseFloat(breakdown.local_transport) || 0,
            installation: parseFloat(breakdown.installation) || 0,
            other: parseFloat(breakdown.other) || 0,
          }
        : null,
      useful_life_years: parseInt(form.useful_life_years) || 5,
      salvage_value_ghs: parseFloat(form.salvage_value_ghs) || 0,
    };
    onSave(payload);
  };

  const labelSx = {
    color: '#888',
    fontSize: '0.72rem',
    fontWeight: 600,
    mb: 0.5,
    display: 'block',
  };
  const inputSx = {
    '& .MuiOutlinedInput-root': {
      bgcolor: '#111',
      color: '#e0e0e0',
      borderRadius: 1.5,
      '& fieldset': { borderColor: 'rgba(255,255,255,0.1)' },
      '&:hover fieldset': { borderColor: 'rgba(255,152,0,0.4)' },
      '&.Mui-focused fieldset': { borderColor: '#ff9800' },
    },
    '& .MuiInputLabel-root': { color: '#666' },
    '& .MuiSelect-icon': { color: '#666' },
  };

  const isConstruction = form.status === 'under_construction';

  const canSave =
    !!form.name &&
    !!form.purchase_date &&
    (isConstruction || (!!form.cost && (form.currency !== 'USD' || !!form.exchange_rate)));

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      scroll="paper"
      slotProps={{
        paper: {
          sx: {
            background: 'linear-gradient(145deg, #1a1a1a 0%, #222 100%)',
            border: '1px solid rgba(255,152,0,0.2)',
            borderRadius: 2.5,
            maxHeight: '90vh',
            display: 'flex',
            flexDirection: 'column',
          },
        },
      }}
    >
      <DialogTitle sx={{ color: '#fff', fontWeight: 700, pb: 1, flexShrink: 0 }}>
        {initial?.id ? 'Edit Investment' : 'Add Capital Investment'}
      </DialogTitle>

      <DialogContent
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
          pt: '8px !important',
          overflowY: 'auto',
        }}
      >
        {/* Status toggle */}
        <Box>
          <Typography component="span" sx={labelSx}>
            Type
          </Typography>
          <ToggleButtonGroup
            exclusive
            value={form.status}
            onChange={(_, val) => val && set('status', val)}
            size="small"
          >
            {[
              { value: 'active', label: 'Active Asset' },
              { value: 'under_construction', label: 'Under Construction' },
            ].map(({ value, label }) => (
              <ToggleButton
                key={value}
                value={value}
                sx={{
                  color: '#555',
                  border: '1px solid rgba(255,255,255,0.1)',
                  fontWeight: 700,
                  px: 2.5,
                  fontSize: '0.78rem',
                  '&.Mui-selected': {
                    bgcolor:
                      value === 'under_construction'
                        ? 'rgba(255,193,7,0.15)'
                        : 'rgba(76,175,80,0.15)',
                    color: value === 'under_construction' ? '#ffc107' : '#4caf50',
                    borderColor:
                      value === 'under_construction'
                        ? 'rgba(255,193,7,0.4)'
                        : 'rgba(76,175,80,0.4)',
                  },
                }}
              >
                {label}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
          {isConstruction && (
            <Box
              sx={{
                mt: 1,
                p: 1.5,
                bgcolor: 'rgba(255,193,7,0.06)',
                borderRadius: 1.5,
                border: '1px solid rgba(255,193,7,0.18)',
              }}
            >
              <Typography variant="caption" sx={{ color: '#ffc107', lineHeight: 1.6 }}>
                Costs will be tracked via milestone payments. Depreciation starts only after marking
                the asset complete.
              </Typography>
            </Box>
          )}
        </Box>

        {/* Name */}
        <Box>
          <Typography component="span" sx={labelSx}>
            Asset Name *
          </Typography>
          <TextField
            fullWidth
            size="small"
            value={form.name}
            onChange={(e) => set('name', e.target.value)}
            placeholder="e.g. Layer cage block A"
            sx={inputSx}
          />
        </Box>

        {/* Category + Date */}
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, sm: 6 }}>
            <Typography component="span" sx={labelSx}>
              Category *
            </Typography>
            <Select
              fullWidth
              size="small"
              value={form.category}
              onChange={(e) => set('category', e.target.value)}
              sx={inputSx}
            >
              {CATEGORIES.map((c) => (
                <MenuItem key={c} value={c}>
                  {c}
                </MenuItem>
              ))}
            </Select>
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <Typography component="span" sx={labelSx}>
              {isConstruction ? 'Start Date *' : 'Purchase Date *'}
            </Typography>
            <TextField
              fullWidth
              size="small"
              type="date"
              value={form.purchase_date}
              onChange={(e) => set('purchase_date', e.target.value)}
              sx={inputSx}
            />
          </Grid>
        </Grid>

        {/* Currency toggle — hidden for under_construction */}
        {!isConstruction && (
          <>
            <Divider sx={{ borderColor: 'rgba(255,255,255,0.06)' }} />
            <Box>
              <Typography component="span" sx={labelSx}>
                Purchase Currency *
              </Typography>
              <ToggleButtonGroup
                exclusive
                value={form.currency}
                onChange={handleCurrencyChange}
                size="small"
              >
                {['GHS', 'USD'].map((c) => (
                  <ToggleButton
                    key={c}
                    value={c}
                    sx={{
                      color: '#555',
                      border: '1px solid rgba(255,255,255,0.1)',
                      fontWeight: 700,
                      px: 3,
                      '&.Mui-selected': {
                        bgcolor: 'rgba(255,152,0,0.15)',
                        color: '#ff9800',
                        borderColor: 'rgba(255,152,0,0.4)',
                      },
                    }}
                  >
                    {c === 'GHS' ? 'GH₵ GHS' : '$ USD'}
                  </ToggleButton>
                ))}
              </ToggleButtonGroup>
            </Box>
          </>
        )}

        <Divider sx={{ borderColor: 'rgba(255,255,255,0.06)' }} />

        {/* Depreciation */}
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, sm: 4 }}>
            <Typography component="span" sx={labelSx}>
              Useful Life (years) *
            </Typography>
            <TextField
              fullWidth
              size="small"
              type="number"
              slotProps={{ htmlInput: { min: 1, max: 99 } }}
              value={form.useful_life_years}
              onChange={(e) => set('useful_life_years', e.target.value)}
              sx={inputSx}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 4 }}>
            <Typography component="span" sx={labelSx}>
              Salvage Value (GHS)
            </Typography>
            <TextField
              fullWidth
              size="small"
              type="number"
              slotProps={{
                htmlInput: { min: 0, step: 0.01 },
                input: {
                  startAdornment: (
                    <InputAdornment position="start">
                      <Typography sx={{ color: '#666', fontSize: '0.8rem' }}>GH₵</Typography>
                    </InputAdornment>
                  ),
                },
              }}
              value={form.salvage_value_ghs}
              onChange={(e) => set('salvage_value_ghs', e.target.value)}
              sx={inputSx}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 4 }}>
            <Typography component="span" sx={labelSx}>
              Depreciation Method *
            </Typography>
            <Select
              fullWidth
              size="small"
              value={form.depreciation_method}
              onChange={(e) => set('depreciation_method', e.target.value)}
              sx={inputSx}
            >
              {DEPRECIATION_METHODS.map((m) => (
                <MenuItem key={m.value} value={m.value}>
                  {m.label}
                </MenuItem>
              ))}
            </Select>
          </Grid>
        </Grid>

        {/* Notes */}
        <Box>
          <Typography component="span" sx={labelSx}>
            Notes
          </Typography>
          <TextField
            fullWidth
            size="small"
            multiline
            rows={2}
            value={form.notes}
            onChange={(e) => set('notes', e.target.value)}
            placeholder="Optional notes…"
            sx={inputSx}
          />
        </Box>

        {!isConstruction && (
          <>
            <Divider sx={{ borderColor: 'rgba(255,255,255,0.06)' }} />

            {/* Cost Breakdown — base price + all landed costs */}
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 2,
                p: 2,
                borderRadius: 2,
                border: '1px solid rgba(255,152,0,0.15)',
                bgcolor: 'rgba(255,152,0,0.03)',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <ShippingIcon sx={{ fontSize: '0.9rem', color: '#ff9800' }} />
                <Typography
                  sx={{
                    fontSize: '0.72rem',
                    fontWeight: 700,
                    color: '#ff9800',
                    letterSpacing: '0.8px',
                  }}
                >
                  COST BREAKDOWN
                </Typography>
              </Box>

              {/* Base Purchase Price */}
              <Grid container spacing={2}>
                <Grid size={{ xs: 12, sm: form.currency === 'USD' ? 6 : 12 }}>
                  <Typography component="span" sx={labelSx}>
                    Base Purchase Price ({form.currency}) *
                  </Typography>
                  <TextField
                    fullWidth
                    size="small"
                    type="number"
                    slotProps={{
                      htmlInput: { min: 0, step: 0.01 },
                      input: {
                        startAdornment: (
                          <InputAdornment position="start">
                            <Typography
                              sx={{
                                color: form.currency === 'USD' ? '#4caf50' : '#666',
                                fontSize: '0.8rem',
                                fontWeight: form.currency === 'USD' ? 700 : 400,
                              }}
                            >
                              {form.currency === 'USD' ? '$' : 'GH₵'}
                            </Typography>
                          </InputAdornment>
                        ),
                      },
                    }}
                    value={form.cost}
                    onChange={(e) => set('cost', e.target.value)}
                    sx={inputSx}
                  />
                </Grid>
                {form.currency === 'USD' && (
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <Typography component="span" sx={labelSx}>
                      Exchange Rate (GHS/USD) *
                    </Typography>
                    <TextField
                      fullWidth
                      size="small"
                      type="number"
                      slotProps={{ htmlInput: { min: 0, step: 0.01 } }}
                      value={form.exchange_rate}
                      onChange={(e) => set('exchange_rate', e.target.value)}
                      placeholder="e.g. 15.40"
                      sx={inputSx}
                    />
                  </Grid>
                )}
              </Grid>
              {form.currency === 'USD' && baseGhs > 0 && (
                <Typography
                  variant="caption"
                  sx={{ color: '#ff9800', fontWeight: 600, pl: 0.5, mt: -1 }}
                >
                  ≈ {formatCurrency(baseGhs)} GHS at current rate
                </Typography>
              )}

              <Divider sx={{ borderColor: 'rgba(255,255,255,0.06)' }} />

              <Typography variant="caption" sx={{ color: '#555' }}>
                Landed costs — shipping, duties, and charges to bring the asset to working
                condition. Leave blank if not applicable.
              </Typography>

              <Grid container spacing={2}>
                {/* Shipping — always USD */}
                <Grid size={{ xs: 12, sm: form.currency === 'GHS' ? 6 : 12 }}>
                  <Typography component="span" sx={labelSx}>
                    Shipping / Freight (USD)
                  </Typography>
                  <TextField
                    fullWidth
                    size="small"
                    type="number"
                    slotProps={{
                      htmlInput: { min: 0, step: 0.01 },
                      input: {
                        startAdornment: (
                          <InputAdornment position="start">
                            <Typography
                              sx={{ color: '#4caf50', fontSize: '0.75rem', fontWeight: 700 }}
                            >
                              $
                            </Typography>
                          </InputAdornment>
                        ),
                      },
                    }}
                    value={breakdown.shipping}
                    onChange={(e) => setBd('shipping', e.target.value)}
                    placeholder="0.00"
                    sx={inputSx}
                  />
                  {breakdown.shipping && effectiveShippingRate > 0 && (
                    <Typography variant="caption" sx={{ color: '#555', pl: 0.5 }}>
                      ≈{' '}
                      {formatCurrency(
                        (parseFloat(breakdown.shipping) || 0) * effectiveShippingRate
                      )}{' '}
                      GHS
                    </Typography>
                  )}
                  {breakdown.shipping && effectiveShippingRate === 0 && (
                    <Typography variant="caption" sx={{ color: '#f44336', pl: 0.5 }}>
                      Enter rate below to convert
                    </Typography>
                  )}
                </Grid>

                {/* Inline rate — only needed when base currency is GHS */}
                {form.currency === 'GHS' && (
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <Typography component="span" sx={labelSx}>
                      Shipping Rate (GHS/USD)
                    </Typography>
                    <TextField
                      fullWidth
                      size="small"
                      type="number"
                      slotProps={{ htmlInput: { min: 0, step: 0.01 } }}
                      value={breakdown.shipping_rate}
                      onChange={(e) => setBd('shipping_rate', e.target.value)}
                      placeholder="e.g. 15.40"
                      sx={inputSx}
                    />
                  </Grid>
                )}

                {/* GHS fields */}
                {(
                  [
                    'import_duty',
                    'clearing_fees',
                    'local_transport',
                    'installation',
                    'other',
                  ] as const
                ).map((key) => (
                  <Grid key={key} size={{ xs: 12, sm: 6 }}>
                    <Typography component="span" sx={labelSx}>
                      {BREAKDOWN_LABELS[key]}
                    </Typography>
                    <TextField
                      fullWidth
                      size="small"
                      type="number"
                      slotProps={{
                        htmlInput: { min: 0, step: 0.01 },
                        input: {
                          startAdornment: (
                            <InputAdornment position="start">
                              <Typography sx={{ color: '#666', fontSize: '0.75rem' }}>
                                GH₵
                              </Typography>
                            </InputAdornment>
                          ),
                        },
                      }}
                      value={breakdown[key]}
                      onChange={(e) => setBd(key, e.target.value)}
                      placeholder="0.00"
                      sx={inputSx}
                    />
                  </Grid>
                ))}
              </Grid>

              {/* Total receipt — always shown when base cost is entered */}
              {totalGhs > 0 && (
                <Box
                  sx={{
                    px: 2,
                    py: 1.5,
                    bgcolor: 'rgba(255,152,0,0.07)',
                    borderRadius: 1.5,
                    border: '1px solid rgba(255,152,0,0.15)',
                  }}
                >
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                    <Typography variant="caption" sx={{ color: '#666' }}>
                      Base price
                    </Typography>
                    <Typography variant="caption" sx={{ color: '#aaa', fontWeight: 600 }}>
                      {formatCurrency(baseGhs)}
                    </Typography>
                  </Box>
                  {(parseFloat(breakdown.shipping) || 0) > 0 && effectiveShippingRate > 0 && (
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.25 }}>
                      <Typography variant="caption" sx={{ color: '#555' }}>
                        + Shipping ({formatUSD(breakdown.shipping)} @{' '}
                        {effectiveShippingRate.toFixed(2)})
                      </Typography>
                      <Typography variant="caption" sx={{ color: '#777' }}>
                        {formatCurrency(
                          (parseFloat(breakdown.shipping) || 0) * effectiveShippingRate
                        )}
                      </Typography>
                    </Box>
                  )}
                  {(
                    [
                      'import_duty',
                      'clearing_fees',
                      'local_transport',
                      'installation',
                      'other',
                    ] as const
                  ).map((key) => {
                    const n = parseFloat(breakdown[key]) || 0;
                    if (!n) return null;
                    return (
                      <Box
                        key={key}
                        sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.25 }}
                      >
                        <Typography variant="caption" sx={{ color: '#555' }}>
                          + {BREAKDOWN_LABELS[key]}
                        </Typography>
                        <Typography variant="caption" sx={{ color: '#777' }}>
                          {formatCurrency(n)}
                        </Typography>
                      </Box>
                    );
                  })}
                  <Divider sx={{ borderColor: 'rgba(255,255,255,0.08)', my: 0.75 }} />
                  <Box
                    sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
                  >
                    <Typography variant="caption" sx={{ color: '#888' }}>
                      Total landed cost
                    </Typography>
                    <Typography sx={{ color: '#ff9800', fontWeight: 800, fontSize: '1.05rem' }}>
                      {formatCurrency(totalGhs)}
                    </Typography>
                  </Box>
                </Box>
              )}
            </Box>
          </>
        )}
      </DialogContent>

      <DialogActions
        sx={{
          px: 3,
          pb: 2.5,
          gap: 1,
          flexShrink: 0,
          borderTop: '1px solid rgba(255,255,255,0.06)',
        }}
      >
        <Button
          onClick={onClose}
          sx={{ color: '#555', '&:hover': { bgcolor: 'rgba(255,255,255,0.05)', color: '#888' } }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={saving || !canSave}
          sx={{
            background: 'linear-gradient(135deg, #ff9800, #f57c00)',
            color: '#000',
            fontWeight: 700,
            px: 3,
            '&:hover': { background: 'linear-gradient(135deg, #ffa726, #ff9800)' },
          }}
        >
          {saving ? 'Saving…' : initial?.id ? 'Save Changes' : 'Add Investment'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// ── Add Cost dialog ────────────────────────────────────────────────────────

const AddCostDialog: React.FC<{
  row: any | null;
  onClose: () => void;
  onSave: (id: number, newCostGhs: number, mergedBreakdown: Record<string, number>) => void;
  saving: boolean;
}> = ({ row, onClose, onSave, saving }) => {
  const [additions, setAdditions] = useState<typeof EMPTY_BREAKDOWN>(EMPTY_BREAKDOWN);

  React.useEffect(() => {
    if (row) setAdditions(EMPTY_BREAKDOWN);
  }, [row]);

  const setField = (key: string, val: string) => setAdditions((a) => ({ ...a, [key]: val }));

  // use the stored shipping rate from the record (preferred) or the base exchange rate
  const shippingRate =
    parseFloat(row?.cost_breakdown?.shipping_rate) || parseFloat(row?.exchange_rate) || 0;
  const additionSum = sumBreakdownGhs(additions, shippingRate);
  const hasAny = additionSum > 0;

  const handleSave = () => {
    const existing: Record<string, number> = row?.cost_breakdown ?? {};
    const merged: Record<string, number> = {};
    for (const key of Object.keys(EMPTY_BREAKDOWN)) {
      const addedVal = parseFloat(additions[key as keyof typeof EMPTY_BREAKDOWN]) || 0;
      // shipping stays in USD; shipping_rate is metadata not summed
      merged[key] =
        key === 'shipping_rate' ? existing[key] || shippingRate : (existing[key] || 0) + addedVal;
    }
    const newCostGhs = parseFloat(row.cost_ghs) + additionSum;
    onSave(row.id, newCostGhs, merged);
  };

  const labelSx = {
    color: '#888',
    fontSize: '0.72rem',
    fontWeight: 600,
    mb: 0.5,
    display: 'block',
  };
  const inputSx = {
    '& .MuiOutlinedInput-root': {
      bgcolor: '#111',
      color: '#e0e0e0',
      borderRadius: 1.5,
      '& fieldset': { borderColor: 'rgba(255,255,255,0.1)' },
      '&:hover fieldset': { borderColor: 'rgba(255,152,0,0.4)' },
      '&.Mui-focused fieldset': { borderColor: '#ff9800' },
    },
  };

  return (
    <Dialog
      open={!!row}
      onClose={onClose}
      maxWidth="xs"
      fullWidth
      slotProps={{
        paper: {
          sx: {
            background: 'linear-gradient(145deg, #1a1a1a 0%, #222 100%)',
            border: '1px solid rgba(255,152,0,0.2)',
            borderRadius: 2.5,
          },
        },
      }}
    >
      <DialogTitle sx={{ pb: 0.5 }}>
        <Typography fontWeight={700} sx={{ color: '#fff' }}>
          Add Cost
        </Typography>
        <Typography variant="caption" sx={{ color: '#555' }}>
          {row?.name}
        </Typography>
      </DialogTitle>

      <DialogContent
        sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, pt: '8px !important' }}
      >
        <Typography variant="caption" sx={{ color: '#555', lineHeight: 1.6 }}>
          Enter only the new costs to add — existing amounts are preserved. All fields in GHS.
        </Typography>

        <Grid container spacing={2}>
          {(Object.keys(EMPTY_BREAKDOWN) as (keyof typeof EMPTY_BREAKDOWN)[])
            .filter((key) => key !== 'shipping_rate')
            .map((key) => {
              const isUsd = key === 'shipping';
              return (
                <Grid key={key} size={{ xs: 12, sm: 6 }}>
                  <Typography component="span" sx={labelSx}>
                    {BREAKDOWN_LABELS[key]}
                  </Typography>
                  <TextField
                    fullWidth
                    size="small"
                    type="number"
                    slotProps={{
                      htmlInput: { min: 0, step: 0.01 },
                      input: {
                        startAdornment: (
                          <InputAdornment position="start">
                            <Typography
                              sx={{
                                color: isUsd ? '#4caf50' : '#666',
                                fontSize: '0.75rem',
                                fontWeight: isUsd ? 700 : 400,
                              }}
                            >
                              {isUsd ? '$' : 'GH₵'}
                            </Typography>
                          </InputAdornment>
                        ),
                      },
                    }}
                    value={additions[key]}
                    onChange={(e) => setField(key, e.target.value)}
                    placeholder="0.00"
                    sx={inputSx}
                  />
                </Grid>
              );
            })}
        </Grid>

        {hasAny && (
          <Box
            sx={{
              px: 2,
              py: 1.5,
              bgcolor: 'rgba(255,152,0,0.07)',
              borderRadius: 1.5,
              border: '1px solid rgba(255,152,0,0.18)',
            }}
          >
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
              <Typography variant="caption" sx={{ color: '#888' }}>
                Current book cost
              </Typography>
              <Typography variant="caption" sx={{ color: '#aaa', fontWeight: 600 }}>
                {formatCurrency(parseFloat(row?.cost_ghs ?? 0))}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
              <Typography variant="caption" sx={{ color: '#888' }}>
                Adding now
              </Typography>
              <Typography variant="caption" sx={{ color: '#ff9800', fontWeight: 600 }}>
                + {formatCurrency(additionSum)}
              </Typography>
            </Box>
            <Divider sx={{ borderColor: 'rgba(255,255,255,0.08)', my: 0.75 }} />
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="caption" sx={{ color: '#888' }}>
                New total cost (GHS)
              </Typography>
              <Typography sx={{ color: '#ff9800', fontWeight: 800, fontSize: '1.05rem' }}>
                {formatCurrency(parseFloat(row?.cost_ghs ?? 0) + additionSum)}
              </Typography>
            </Box>
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
        <Button
          onClick={onClose}
          sx={{ color: '#555', '&:hover': { bgcolor: 'rgba(255,255,255,0.05)', color: '#888' } }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={saving || !hasAny}
          sx={{
            background: 'linear-gradient(135deg, #ff9800, #f57c00)',
            color: '#000',
            fontWeight: 700,
            px: 3,
            '&:hover': { background: 'linear-gradient(135deg, #ffa726, #ff9800)' },
          }}
        >
          {saving ? 'Saving…' : 'Add Cost'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// ── Mark Complete dialog ────────────────────────────────────────────────────

const CompleteInvestmentDialog: React.FC<{
  investment: any | null;
  onClose: () => void;
  onDone: () => void;
}> = ({ investment, onClose, onDone }) => {
  const completeMutation = useCompleteInvestment();
  const [form, setForm] = useState({
    completion_date: new Date().toISOString().slice(0, 10),
    cost_ghs: '',
    useful_life_years: '10',
    salvage_value_ghs: '0',
    depreciation_method: 'straight_line',
    notes: '',
  });

  React.useEffect(() => {
    if (investment) {
      setForm({
        completion_date: new Date().toISOString().slice(0, 10),
        cost_ghs: String(investment.total_paid_ghs ?? '0'),
        useful_life_years: String(investment.useful_life_years ?? '10'),
        salvage_value_ghs: String(investment.salvage_value_ghs ?? '0'),
        depreciation_method: investment.depreciation_method ?? 'straight_line',
        notes: investment.notes ?? '',
      });
    }
  }, [investment]);

  const set = (f: string, v: string) => setForm((p) => ({ ...p, [f]: v }));

  const handleComplete = () => {
    completeMutation.mutate(
      {
        id: investment.id,
        data: {
          completion_date: form.completion_date,
          cost_ghs: parseFloat(form.cost_ghs) || 0,
          useful_life_years: parseInt(form.useful_life_years) || 10,
          salvage_value_ghs: parseFloat(form.salvage_value_ghs) || 0,
          depreciation_method: form.depreciation_method,
          notes: form.notes || null,
        },
      },
      {
        onSuccess: () => {
          onDone();
          onClose();
        },
      }
    );
  };

  const labelSx = {
    color: '#888',
    fontSize: '0.72rem',
    fontWeight: 600,
    mb: 0.5,
    display: 'block',
  };
  const inputSx = {
    '& .MuiOutlinedInput-root': {
      bgcolor: '#111',
      color: '#e0e0e0',
      borderRadius: 1.5,
      '& fieldset': { borderColor: 'rgba(255,255,255,0.1)' },
      '&:hover fieldset': { borderColor: 'rgba(76,175,80,0.4)' },
      '&.Mui-focused fieldset': { borderColor: '#4caf50' },
    },
    '& .MuiInputLabel-root': { color: '#666' },
    '& .MuiSelect-icon': { color: '#666' },
  };

  const totalPaid = parseFloat(investment?.total_paid_ghs ?? 0);

  return (
    <Dialog
      open={!!investment}
      onClose={onClose}
      maxWidth="xs"
      fullWidth
      slotProps={{
        paper: {
          sx: {
            background: 'linear-gradient(145deg, #1a1a1a 0%, #222 100%)',
            border: '1px solid rgba(76,175,80,0.3)',
            borderRadius: 2.5,
          },
        },
      }}
    >
      <DialogTitle sx={{ pb: 0.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <CheckCircleIcon sx={{ color: '#4caf50', fontSize: '1.2rem' }} />
          <Box>
            <Typography fontWeight={700} sx={{ color: '#fff', lineHeight: 1.2 }}>
              Mark as Complete
            </Typography>
            <Typography variant="caption" sx={{ color: '#555' }}>
              {investment?.name}
            </Typography>
          </Box>
        </Box>
      </DialogTitle>

      <DialogContent
        sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '8px !important' }}
      >
        {totalPaid > 0 && (
          <Box
            sx={{
              p: 1.5,
              bgcolor: 'rgba(76,175,80,0.06)',
              borderRadius: 1.5,
              border: '1px solid rgba(76,175,80,0.18)',
            }}
          >
            <Typography variant="caption" sx={{ color: '#4caf50' }}>
              Total milestone payments recorded: <strong>{formatCurrency(totalPaid)}</strong> —
              pre-filled as the final cost below.
            </Typography>
          </Box>
        )}

        <Box>
          <Typography component="span" sx={labelSx}>
            Completion Date *
          </Typography>
          <TextField
            fullWidth
            size="small"
            type="date"
            value={form.completion_date}
            onChange={(e) => set('completion_date', e.target.value)}
            sx={inputSx}
          />
        </Box>

        <Box>
          <Typography component="span" sx={labelSx}>
            Final Cost (GHS) *
          </Typography>
          <TextField
            fullWidth
            size="small"
            type="number"
            slotProps={{
              htmlInput: { min: 0, step: 0.01 },
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <Typography sx={{ color: '#666', fontSize: '0.8rem' }}>GH₵</Typography>
                  </InputAdornment>
                ),
              },
            }}
            value={form.cost_ghs}
            onChange={(e) => set('cost_ghs', e.target.value)}
            sx={inputSx}
          />
        </Box>

        <Grid container spacing={2}>
          <Grid size={{ xs: 12, sm: 6 }}>
            <Typography component="span" sx={labelSx}>
              Useful Life (years) *
            </Typography>
            <TextField
              fullWidth
              size="small"
              type="number"
              slotProps={{ htmlInput: { min: 1, max: 99 } }}
              value={form.useful_life_years}
              onChange={(e) => set('useful_life_years', e.target.value)}
              sx={inputSx}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <Typography component="span" sx={labelSx}>
              Salvage Value (GHS)
            </Typography>
            <TextField
              fullWidth
              size="small"
              type="number"
              slotProps={{ htmlInput: { min: 0, step: 0.01 } }}
              value={form.salvage_value_ghs}
              onChange={(e) => set('salvage_value_ghs', e.target.value)}
              sx={inputSx}
            />
          </Grid>
        </Grid>

        <Box>
          <Typography component="span" sx={labelSx}>
            Depreciation Method *
          </Typography>
          <Select
            fullWidth
            size="small"
            value={form.depreciation_method}
            onChange={(e) => set('depreciation_method', e.target.value)}
            sx={inputSx}
          >
            {DEPRECIATION_METHODS.map((m) => (
              <MenuItem key={m.value} value={m.value}>
                {m.label}
              </MenuItem>
            ))}
          </Select>
        </Box>

        <Box>
          <Typography component="span" sx={labelSx}>
            Notes
          </Typography>
          <TextField
            fullWidth
            size="small"
            multiline
            rows={2}
            value={form.notes}
            onChange={(e) => set('notes', e.target.value)}
            sx={inputSx}
          />
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2.5, gap: 1, borderTop: '1px solid rgba(255,255,255,0.06)' }}>
        <Button
          onClick={onClose}
          sx={{ color: '#555', '&:hover': { bgcolor: 'rgba(255,255,255,0.05)', color: '#888' } }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleComplete}
          disabled={completeMutation.isPending || !form.completion_date || !form.cost_ghs}
          sx={{
            background: 'linear-gradient(135deg, #4caf50, #388e3c)',
            color: '#fff',
            fontWeight: 700,
            px: 3,
            '&:hover': { background: 'linear-gradient(135deg, #66bb6a, #4caf50)' },
          }}
        >
          {completeMutation.isPending ? 'Completing…' : 'Mark Complete'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// ── Main page ──────────────────────────────────────────────────────────────

const CapitalInvestments: React.FC = () => {
  const navigate = useNavigate();
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<any>(null);
  const [deleteTarget, setDeleteTarget] = useState<any>(null);
  const [addCostTarget, setAddCostTarget] = useState<any>(null);
  const [completeTarget, setCompleteTarget] = useState<any>(null);
  const [snack, setSnack] = useState<{ msg: string; severity: 'success' | 'error' } | null>(null);

  const { data: rawData = [], isFetching } = useCapitalInvestments();
  const investments = rawData as any[];

  const createMutation = useCreateCapitalInvestment();
  const updateMutation = useUpdateCapitalInvestment();
  const deleteMutation = useDeleteCapitalInvestment();

  const stats = useMemo(() => {
    const active = investments.filter((i) => i.status !== 'under_construction');
    const cwip = investments.filter((i) => i.status === 'under_construction');
    const totalInvested = active.reduce((s, i) => s + parseFloat(i.cost_ghs), 0);
    const totalBookValue = active.reduce((s, i) => s + parseFloat(i.current_book_value_ghs), 0);
    const totalDepreciated = active.reduce(
      (s, i) => s + parseFloat(i.accumulated_depreciation_ghs),
      0
    );
    const cwipValue = cwip.reduce((s, i) => s + parseFloat(i.current_book_value_ghs), 0);
    return {
      totalInvested,
      totalBookValue,
      totalDepreciated,
      count: active.length,
      cwipCount: cwip.length,
      cwipValue,
    };
  }, [investments]);

  const openAdd = () => {
    setEditing(null);
    setFormOpen(true);
  };

  const openEdit = (row: any) => {
    setEditing({
      ...row,
      purchase_date: row.purchase_date?.substring(0, 10) ?? '',
      cost: String(row.cost),
      exchange_rate: row.exchange_rate ? String(row.exchange_rate) : '',
      cost_ghs: String(row.cost_ghs),
      useful_life_years: String(row.useful_life_years),
      salvage_value_ghs: String(row.salvage_value_ghs),
    });
    setFormOpen(true);
  };

  const handleSave = (data: any) => {
    if (editing?.id) {
      updateMutation.mutate(
        { id: editing.id, data },
        {
          onSuccess: () => {
            setFormOpen(false);
            setSnack({ msg: 'Investment updated.', severity: 'success' });
          },
          onError: () => setSnack({ msg: 'Failed to update.', severity: 'error' }),
        }
      );
    } else {
      createMutation.mutate(data, {
        onSuccess: () => {
          setFormOpen(false);
          setSnack({ msg: 'Investment added.', severity: 'success' });
        },
        onError: () => setSnack({ msg: 'Failed to add investment.', severity: 'error' }),
      });
    }
  };

  const handleAddCost = (
    id: number,
    newCostGhs: number,
    mergedBreakdown: Record<string, number>
  ) => {
    updateMutation.mutate(
      { id, data: { cost_ghs: newCostGhs, cost_breakdown: mergedBreakdown } },
      {
        onSuccess: () => {
          setAddCostTarget(null);
          setSnack({ msg: 'Cost added successfully.', severity: 'success' });
        },
        onError: () => setSnack({ msg: 'Failed to add cost.', severity: 'error' }),
      }
    );
  };

  const handleDelete = () => {
    if (!deleteTarget) return;
    deleteMutation.mutate(deleteTarget.id, {
      onSuccess: () => {
        setDeleteTarget(null);
        setSnack({ msg: 'Investment removed.', severity: 'success' });
      },
      onError: () => setSnack({ msg: 'Failed to delete.', severity: 'error' }),
    });
  };

  const columns: GridColDef[] = [
    {
      field: 'name',
      headerName: 'Asset',
      flex: 1.5,
      minWidth: 160,
      renderCell: (p: GridRenderCellParams) => {
        const proc = p.row.procurement;
        const birdSubtitle = proc
          ? `${(proc.qty_white_birds_procured ?? 0) + (proc.qty_brown_birds_procured ?? 0)} birds — ${proc.qty_white_birds_procured ?? 0} white, ${proc.qty_brown_birds_procured ?? 0} brown`
          : null;
        const subtitle = birdSubtitle ?? p.row.notes;
        return (
          <Box>
            <Typography variant="body2" sx={{ fontWeight: 700, color: '#e0e0e0', lineHeight: 1.3 }}>
              {p.value}
            </Typography>
            {subtitle && (
              <Typography variant="caption" sx={{ color: '#555', display: 'block' }} noWrap>
                {subtitle}
              </Typography>
            )}
          </Box>
        );
      },
    },
    {
      field: 'category',
      headerName: 'Category',
      width: 140,
      renderCell: (p: GridRenderCellParams) => {
        const cfg = CATEGORY_CONFIG[p.value] ?? { color: '#888', bg: 'rgba(136,136,136,0.12)' };
        return (
          <Chip
            label={p.value}
            size="small"
            sx={{
              bgcolor: cfg.bg,
              color: cfg.color,
              fontWeight: 700,
              fontSize: '0.68rem',
              border: `1px solid ${cfg.color}33`,
            }}
          />
        );
      },
    },
    {
      field: 'purchase_date',
      headerName: 'Purchased',
      width: 120,
      renderCell: (p: GridRenderCellParams) => (
        <Typography variant="caption" sx={{ color: '#888' }}>
          {fmtDate(p.value)}
        </Typography>
      ),
    },
    {
      field: 'cost',
      headerName: 'Original Cost',
      width: 165,
      renderCell: (p: GridRenderCellParams) => {
        const breakdownEntries = Object.entries(p.row.cost_breakdown ?? {}).filter(
          ([k, v]) => k !== 'shipping_rate' && Number(v) > 0
        );
        const hasBreakdown = breakdownEntries.length > 0;
        return (
          <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
            <Typography
              variant="body2"
              sx={{ fontWeight: 700, color: '#fff', fontSize: '0.82rem' }}
            >
              {p.row.currency === 'USD' ? formatUSD(p.value) : formatCurrency(p.value)}
            </Typography>
            {p.row.currency === 'USD' && (
              <Typography variant="caption" sx={{ color: '#666', display: 'block' }}>
                {formatCurrency(p.row.cost_ghs)} @ {parseFloat(p.row.exchange_rate).toFixed(2)}
              </Typography>
            )}
            {hasBreakdown && (
              <Tooltip
                title={
                  <Box sx={{ p: 0.5 }}>
                    {breakdownEntries.map(([k, v]) => (
                      <Box
                        key={k}
                        sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}
                      >
                        <Typography variant="caption">{BREAKDOWN_LABELS[k] ?? k}</Typography>
                        <Typography variant="caption" sx={{ fontWeight: 600 }}>
                          {k === 'shipping' ? formatUSD(Number(v)) : formatCurrency(Number(v))}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                }
              >
                <Typography
                  variant="caption"
                  sx={{ color: '#ff9800', cursor: 'default', fontSize: '0.65rem' }}
                >
                  + landed costs included
                </Typography>
              </Tooltip>
            )}
          </Box>
        );
      },
    },
    {
      field: 'current_book_value_ghs',
      headerName: 'Book Value',
      width: 180,
      renderCell: (p: GridRenderCellParams) => {
        const bv = parseFloat(p.value);
        const cost = parseFloat(p.row.cost_ghs);
        const pct = cost > 0 ? (bv / cost) * 100 : 0;
        const color = pct >= 60 ? '#4caf50' : pct >= 30 ? '#ff9800' : '#f44336';
        return (
          <Box sx={{ width: '100%', pr: 1 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
              <Typography variant="body2" sx={{ fontWeight: 700, color, fontSize: '0.8rem' }}>
                {formatCurrency(bv)}
              </Typography>
              <Typography variant="caption" sx={{ color: '#555' }}>
                {pct.toFixed(0)}%
              </Typography>
            </Box>
            <LinearProgress
              variant="determinate"
              value={pct}
              sx={{
                height: 4,
                borderRadius: 2,
                bgcolor: 'rgba(255,255,255,0.06)',
                '& .MuiLinearProgress-bar': { bgcolor: color, borderRadius: 2 },
              }}
            />
          </Box>
        );
      },
    },
    {
      field: 'useful_life_years',
      headerName: 'Life',
      width: 80,
      renderCell: (p: GridRenderCellParams) => (
        <Typography variant="caption" sx={{ color: '#888' }}>
          {p.value} yrs
        </Typography>
      ),
    },
    {
      field: 'depreciation_method',
      headerName: 'Method',
      width: 130,
      renderCell: (p: GridRenderCellParams) => (
        <Typography variant="caption" sx={{ color: '#888' }}>
          {p.value === 'straight_line' ? 'Straight Line' : 'Declining Bal.'}
        </Typography>
      ),
    },
    {
      field: 'status',
      headerName: 'Status',
      width: 160,
      renderCell: (p: GridRenderCellParams) =>
        p.value === 'under_construction' ? (
          <Chip
            icon={<ConstructionIcon sx={{ fontSize: '0.85rem !important' }} />}
            label="Under Construction"
            size="small"
            sx={{
              bgcolor: 'rgba(255,193,7,0.12)',
              color: '#ffc107',
              fontWeight: 700,
              fontSize: '0.65rem',
              border: '1px solid rgba(255,193,7,0.3)',
            }}
          />
        ) : (
          <Chip
            label="Active"
            size="small"
            sx={{
              bgcolor: 'rgba(76,175,80,0.1)',
              color: '#4caf50',
              fontWeight: 700,
              fontSize: '0.68rem',
              border: '1px solid rgba(76,175,80,0.25)',
            }}
          />
        ),
    },
    {
      field: 'actions',
      headerName: '',
      width: 150,
      sortable: false,
      renderCell: (p: GridRenderCellParams) => {
        const isCwip = p.row.status === 'under_construction';
        return (
          <Box sx={{ display: 'flex', gap: 0.5 }}>
            {isCwip ? (
              <>
                <Tooltip title="View Detail">
                  <IconButton
                    size="small"
                    onClick={() => navigate(`/capital-investments/${p.row.id}`)}
                    sx={{ color: '#42a5f5', '&:hover': { bgcolor: 'rgba(66,165,245,0.1)' } }}
                  >
                    <OpenDetailIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Mark Complete">
                  <IconButton
                    size="small"
                    onClick={() => setCompleteTarget(p.row)}
                    sx={{ color: '#4caf50', '&:hover': { bgcolor: 'rgba(76,175,80,0.1)' } }}
                  >
                    <CheckCircleIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </>
            ) : (
              <Tooltip title="Add Cost">
                <IconButton
                  size="small"
                  onClick={() => setAddCostTarget(p.row)}
                  sx={{ color: '#4caf50', '&:hover': { bgcolor: 'rgba(76,175,80,0.1)' } }}
                >
                  <AddCostIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
            <Tooltip title="Edit">
              <IconButton
                size="small"
                onClick={() => openEdit(p.row)}
                sx={{ color: '#ff9800', '&:hover': { bgcolor: 'rgba(255,152,0,0.1)' } }}
              >
                <EditIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Delete">
              <IconButton
                size="small"
                onClick={() => setDeleteTarget(p.row)}
                sx={{ color: '#f44336', '&:hover': { bgcolor: 'rgba(244,67,54,0.1)' } }}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        );
      },
    },
  ];

  const isSaving = createMutation.isPending || updateMutation.isPending;

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      {isFetching && (
        <LinearProgress
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            zIndex: 9999,
            height: 2,
            bgcolor: 'transparent',
            '& .MuiLinearProgress-bar': { background: 'linear-gradient(90deg, #ff9800, #ffcc02)' },
          }}
        />
      )}

      {/* Header */}
      <Box
        sx={{
          mb: 3,
          p: 3,
          borderRadius: 2.5,
          background: 'linear-gradient(135deg, #141414 0%, #1c1c1c 100%)',
          border: '1px solid rgba(255,152,0,0.18)',
          display: 'flex',
          alignItems: 'center',
          gap: 2.5,
        }}
      >
        <Avatar
          sx={{
            bgcolor: 'rgba(255,152,0,0.12)',
            color: '#ff9800',
            width: 56,
            height: 56,
            border: '1px solid rgba(255,152,0,0.25)',
          }}
        >
          <InvestmentIcon />
        </Avatar>
        <Box flex={1}>
          <Typography
            variant="h5"
            fontWeight={800}
            sx={{ color: '#fff', letterSpacing: '-0.02em' }}
          >
            Capital Investments
          </Typography>
          <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.38)', mt: 0.4 }}>
            Track assets, depreciation, and current book value across GHS and USD purchases
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={openAdd}
          sx={{
            background: 'linear-gradient(135deg, #ff9800, #f57c00)',
            color: '#000',
            fontWeight: 700,
            '&:hover': { background: 'linear-gradient(135deg, #ffa726, #ff9800)' },
          }}
        >
          Add Investment
        </Button>
      </Box>

      {/* Stats */}
      <Grid container spacing={2} mb={3}>
        <Grid size={{ xs: 6, sm: 3 }}>
          <StatCard
            label="Total Invested"
            value={formatCurrency(stats.totalInvested)}
            sub={`${stats.count} active asset${stats.count !== 1 ? 's' : ''}`}
            accent="#ff9800"
            Icon={InvestmentIcon}
          />
        </Grid>
        <Grid size={{ xs: 6, sm: 3 }}>
          <StatCard
            label="Current Book Value"
            value={formatCurrency(stats.totalBookValue)}
            accent="#4caf50"
            Icon={BookValueIcon}
          />
        </Grid>
        <Grid size={{ xs: 6, sm: 3 }}>
          <StatCard
            label="Accumulated Depreciation"
            value={formatCurrency(stats.totalDepreciated)}
            accent="#f44336"
            Icon={DepreciationIcon}
          />
        </Grid>
        <Grid size={{ xs: 6, sm: 3 }}>
          <StatCard
            label="Under Construction"
            value={formatCurrency(stats.cwipValue)}
            sub={`${stats.cwipCount} project${stats.cwipCount !== 1 ? 's' : ''} in progress`}
            accent="#ffc107"
            Icon={ConstructionIcon}
          />
        </Grid>
      </Grid>

      {/* Table */}
      <Paper
        sx={{ border: '1px solid rgba(255,152,0,0.15)', borderRadius: 2.5, overflow: 'hidden' }}
      >
        <Box
          sx={{
            px: 3,
            py: 2,
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            background: 'linear-gradient(135deg, #161616 0%, #1e1e1e 100%)',
            borderBottom: '1px solid rgba(255,152,0,0.1)',
          }}
        >
          <InvestmentIcon sx={{ color: '#ff9800', fontSize: '1.1rem' }} />
          <Typography variant="subtitle1" fontWeight={700} sx={{ color: '#fff' }}>
            All Investments
          </Typography>
          <Chip
            label={investments.length}
            size="small"
            sx={{ bgcolor: 'rgba(255,152,0,0.12)', color: '#ff9800', fontWeight: 700, height: 20 }}
          />
        </Box>
        <DataGrid
          rows={investments}
          columns={columns}
          autoHeight
          disableRowSelectionOnClick
          getRowHeight={() => 64}
          sx={{
            border: 'none',
            '& .MuiDataGrid-columnHeaders': {
              backgroundColor: 'rgba(255,152,0,0.06)',
              borderBottom: '1px solid rgba(255,152,0,0.15)',
            },
            '& .MuiDataGrid-columnHeaderTitle': {
              fontWeight: 700,
              fontSize: '0.7rem',
              letterSpacing: '0.8px',
              color: 'rgba(255,152,0,0.8)',
              textTransform: 'uppercase',
            },
            '& .MuiDataGrid-row:hover': { backgroundColor: 'rgba(255,152,0,0.04)' },
            '& .MuiDataGrid-cell': {
              borderColor: 'rgba(255,255,255,0.04)',
              display: 'flex',
              alignItems: 'center',
            },
            '& .MuiDataGrid-footerContainer': { borderTop: '1px solid rgba(255,152,0,0.1)' },
          }}
        />
      </Paper>

      {/* Add/Edit form */}
      <InvestmentForm
        open={formOpen}
        initial={editing ?? { ...EMPTY_FORM }}
        onClose={() => setFormOpen(false)}
        onSave={handleSave}
        saving={isSaving}
      />

      {/* Add Cost dialog */}
      <AddCostDialog
        row={addCostTarget}
        onClose={() => setAddCostTarget(null)}
        onSave={handleAddCost}
        saving={updateMutation.isPending}
      />

      {/* Mark Complete dialog */}
      <CompleteInvestmentDialog
        investment={completeTarget}
        onClose={() => setCompleteTarget(null)}
        onDone={() =>
          setSnack({
            msg: 'Investment marked as complete — depreciation now active.',
            severity: 'success',
          })
        }
      />

      {/* Delete confirm */}
      <Dialog
        open={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        maxWidth="xs"
        fullWidth
        slotProps={{
          paper: {
            sx: {
              background: 'linear-gradient(145deg, #1a1a1a 0%, #222 100%)',
              border: '1px solid rgba(244,67,54,0.25)',
              borderRadius: 2.5,
            },
          },
        }}
      >
        <DialogTitle sx={{ color: '#fff', fontWeight: 700 }}>Remove Investment</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ color: 'rgba(255,255,255,0.5)' }}>
            Remove <strong style={{ color: '#ffa726' }}>{deleteTarget?.name}</strong> from records?
            This cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
          <Button onClick={() => setDeleteTarget(null)} sx={{ color: '#555' }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleDelete}
            disabled={deleteMutation.isPending}
            sx={{
              background: 'linear-gradient(135deg, #f44336, #c62828)',
              color: '#fff',
              fontWeight: 700,
              '&:hover': { background: 'linear-gradient(135deg, #ef5350, #f44336)' },
            }}
          >
            {deleteMutation.isPending ? 'Removing…' : 'Remove'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={!!snack}
        autoHideDuration={3500}
        onClose={() => setSnack(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          severity={snack?.severity ?? 'success'}
          onClose={() => setSnack(null)}
          sx={{ fontWeight: 600 }}
        >
          {snack?.msg}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default CapitalInvestments;
